import { useState, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { track, useTrackPageView } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import BookCover from "@/components/books/BookCover";
import PrintInterestButton from "@/components/books/PrintInterestButton";
import BuyButton from "@/components/books/BuyButton";
import PriceBlock from "@/components/books/PriceBlock";
import StickyBuyBar from "@/components/books/StickyBuyBar";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { LEVEL3_BOOKS, LEVEL3_SERIES, LEVEL3_ACCENT as L, findLevel3Book } from "@/data/level3Books";

export default function Level3Product() {
  const { bookSlug } = useParams();
  const book = findLevel3Book(bookSlug);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const touchStartX = useRef(null);

  useTrackPageView(book ? `level3_book${book.book_number}_view` : "level3_book_view_unknown", { slug: bookSlug });
  useSEO({
    title: book ? `${book.title} | LifeIQ Level 3 Book ${book.book_number}` : "LifeIQ Level 3 · Life Design",
    description: book ? book.short_description : "LifeIQ Level 3 · Life Design — real-world life skills for teens and young adults.",
    canonicalPath: `/books/level3/${bookSlug}`,
  });

  if (!book) return <Navigate to="/books/level3" replace />;

  const idx = LEVEL3_BOOKS.findIndex((b) => b.slug === book.slug);
  const prevBook = idx > 0 ? LEVEL3_BOOKS[idx - 1] : null;
  const nextBook = idx < LEVEL3_BOOKS.length - 1 ? LEVEL3_BOOKS[idx + 1] : null;
  const accent = book.accent;

  const totalPreviews = book.preview_assets.length;
  const showPrev = () => setLightboxIndex((i) => (i - 1 + totalPreviews) % totalPreviews);
  const showNext = () => setLightboxIndex((i) => (i + 1) % totalPreviews);
  const onTouchStart = (e) => { touchStartX.current = e.touches?.[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const endX = e.changedTouches?.[0]?.clientX;
    if (endX === undefined) return;
    const delta = endX - touchStartX.current;
    if (Math.abs(delta) > 40) (delta < 0 ? showNext : showPrev)();
    touchStartX.current = null;
  };

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ backgroundColor: L.navy }}>
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-16 md:pt-20 pb-20 md:pb-28 relative">
          <HeroFade delay={0}>
            <div className="flex items-center gap-2 text-[12px] text-white/50 flex-wrap">
              <Link to="/books/level3" className="hover:text-white transition-colors">LifeIQ Level 3 · Life Design</Link>
              <span>/</span>
              <span className="text-white/80">{book.title}</span>
            </div>
          </HeroFade>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 max-w-[320px]">
              <HeroFade delay={0.1}>
                <BookCover book={book} tilt testId="level3-pdp-cover" />
              </HeroFade>
            </div>
            <div className="lg:col-span-8">
              <HeroFade delay={0.15}>
                <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: accent }}>{book.label} · {book.series_step}</div>
              </HeroFade>
              <KineticTitle
                testId="level3-pdp-title"
                className="mt-3 font-serif-d text-[32px] sm:text-[44px] leading-[1.08] text-white"
                lines={[book.title]}
              />
              <HeroFade delay={0.25}>
                <p className="mt-3 text-[15px] sm:text-[16px] font-semibold" style={{ color: accent }}>{book.subtitle}</p>
                <p className="mt-4 text-[16px] sm:text-[17px] text-white/80 leading-[1.7] max-w-2xl">{book.short_description}</p>
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-white/55">
                  <span>{book.pages} pages</span>
                  <span>Digital Edition · PDF</span>
                  <span>{book.tools_count} practical tools</span>
                  <span>For Teens &amp; Young Adults</span>
                </div>
              </HeroFade>
              <HeroFade delay={0.35}>
                <div className="mt-6">
                  <PriceBlock product={book} dark big testId="level3-pdp-price-block" accentColor={accent} offerChipColor={accent} />
                </div>
                <p data-testid="level3-pdp-buy-context" className="mt-3 text-[12px] uppercase tracking-[0.14em] text-white/55 font-semibold">
                  Digital Edition · PDF · Instant protected download
                </p>
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <BuyButton
                    product={book}
                    label={`Buy Digital Book — ${book.price}`}
                    source="level3-product-page-hero"
                    testId="level3-book-buy-button"
                    dark
                    accentColor={accent}
                  />
                  <a
                    href="#look-inside"
                    data-testid="level3-look-inside-cta"
                    className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-6 py-4 rounded-sm text-[13px] font-semibold uppercase tracking-wide hover:bg-white/10 transition-colors duration-200"
                    onClick={() => track("level3_look_inside", { product_id: book.product_id, source: "product-hero" })}
                  >
                    <Eye size={15} /> Look Inside
                  </a>
                  <Link
                    to="/books/level3/complete-series"
                    data-testid="level3-pdp-bundle-cta"
                    className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/40 px-6 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-white/10 transition-colors duration-200 rounded-sm"
                  >
                    See the Complete Series <ArrowRight size={15} />
                  </Link>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3 text-[13px]">
                  <span className="text-white/40 uppercase tracking-wide text-[11px] font-semibold">Printed Edition · Planned</span>
                  <PrintInterestButton
                    mode="single"
                    seriesId="level3"
                    productId={book.product_id}
                    bookTitle={book.title}
                    sourcePage={`level3-pdp-${book.slug}`}
                    testId="level3-pdp-print-interest"
                    accentColor={accent}
                    className="text-white/70 hover:text-white underline underline-offset-2 text-[13px] font-semibold transition-colors"
                  />
                </div>
              </HeroFade>
            </div>
          </div>
        </div>
      </section>

      {/* LOOK INSIDE */}
      <section id="look-inside" style={{ backgroundColor: L.cream }}>
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: accent }}>Look inside</div>
            <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1]" style={{ color: L.navy }}>A preview of real pages.</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="level3-pdp-look-inside-gallery">
            {book.preview_assets.map((src, i) => (
              <Reveal key={src} delay={i * 0.04}>
                <button
                  type="button"
                  onClick={() => { setLightboxIndex(i); track("level3_look_inside", { product_id: book.product_id, source: "gallery-thumbnail", page: i + 1 }); }}
                  data-testid={`level3-pdp-preview-${i}`}
                  className="relative block w-full group cursor-zoom-in"
                >
                  <img
                    src={src}
                    alt={`${book.title} — preview page ${i + 1}`}
                    className="w-full rounded-sm shadow-md border group-hover:opacity-90 transition-opacity duration-200"
                    style={{ borderColor: `${L.navy}14` }}
                    loading="lazy"
                  />
                  <span className="absolute bottom-2 right-2 text-[11px] font-bold px-1.5 py-0.5 rounded-sm text-white" style={{ backgroundColor: `${L.navy}CC` }}>
                    {i + 1} / {totalPreviews}
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
          <p className="mt-4 text-[12px] text-center" style={{ color: L.textMuted }}>Tap any page to view it larger.</p>
        </div>
      </section>

      {/* LOOK INSIDE LIGHTBOX */}
      <Dialog open={lightboxIndex !== null} onOpenChange={(open) => !open && setLightboxIndex(null)}>
        <DialogContent
          data-testid="level3-lightbox"
          className="max-w-3xl w-[92vw] sm:w-full bg-black/95 border-none p-0 overflow-hidden text-white"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {lightboxIndex !== null && (
            <div className="relative flex flex-col items-center justify-center py-8 px-4 sm:px-10" data-testid="level3-lightbox-body">
              <img src={book.preview_assets[lightboxIndex]} alt={`${book.title} — preview page ${lightboxIndex + 1}`} className="max-h-[70vh] w-auto rounded-sm shadow-2xl" />
              <div className="mt-4 flex items-center gap-6">
                <button type="button" data-testid="level3-lightbox-prev" onClick={showPrev} className="text-white/70 hover:text-white transition-colors" aria-label="Previous page">
                  <ChevronLeft size={22} />
                </button>
                <span data-testid="level3-lightbox-position" className="text-[13px] font-semibold text-white/80 uppercase tracking-wide">
                  Page {lightboxIndex + 1} of {totalPreviews}
                </span>
                <button type="button" data-testid="level3-lightbox-next" onClick={showNext} className="text-white/70 hover:text-white transition-colors" aria-label="Next page">
                  <ChevronRight size={22} />
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* WHAT'S INSIDE — chapters/tools */}
      <section className="bg-white">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: L.teal }}>What's inside</div>
            <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1]" style={{ color: L.navy }}>{book.tools_count} practical tools, {book.pages} pages.</h2>
          </Reveal>
          <div className="mt-10 space-y-4" data-testid="level3-pdp-chapter-list">
            {book.chapters.map((ch) => (
              <div key={ch.name} className="flex items-start gap-3 py-3 border-b" style={{ borderColor: `${L.navy}12` }}>
                <CheckCircle2 size={18} className="mt-0.5 shrink-0" style={{ color: accent }} />
                <div>
                  <div className="text-[15px] font-semibold" style={{ color: L.navy }}>{ch.name}</div>
                  <div className="text-[14px]" style={{ color: L.textMuted }}>{ch.desc}</div>
                </div>
              </div>
            ))}
          </div>
          {book.closesWith && (
            <Reveal delay={0.1}>
              <div className="mt-8 border rounded-sm p-5" style={{ borderColor: `${accent}44`, backgroundColor: L.cream }}>
                <div className="text-[12px] uppercase tracking-[0.14em] font-bold" style={{ color: accent }}>Closes the Level 3 journey with</div>
                <div className="mt-2 flex flex-wrap gap-3">
                  {book.closesWith.map((item) => (
                    <span key={item} className="text-[14px] font-serif-d" style={{ color: L.navy }}>{item}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
          {book.disclaimer && (
            <Reveal delay={0.15}>
              <p data-testid="level3-pdp-disclaimer" className="mt-8 text-[13px] leading-[1.6] italic" style={{ color: L.textMuted }}>{book.disclaimer}</p>
            </Reveal>
          )}
          {book.updatesUrl && (
            <Reveal delay={0.18}>
              <Link to={book.updatesUrl} data-testid="level3-pdp-updates-link" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide" style={{ color: L.teal }}>
                Sources &amp; Updates for this book <ArrowRight size={12} />
              </Link>
            </Reveal>
          )}
        </div>
      </section>

      {/* WHERE IT FITS IN LEVEL 3 */}
      <section style={{ backgroundColor: L.navy }}>
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8 py-16 md:py-20">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold text-center" style={{ color: L.gold }}>Where it fits</div>
            <h2 className="mt-3 font-serif-d text-[24px] sm:text-[30px] text-white text-center leading-[1.15]">Where This Book Fits in Level 3</h2>
          </Reveal>
          <div data-testid="level3-pdp-progression" className="mt-10 flex flex-wrap items-center justify-center gap-x-2 gap-y-4">
            {LEVEL3_SERIES.progression.map((step, i) => {
              const isCurrent = i === book.book_number - 1;
              return (
                <div key={step} className="flex items-center gap-2">
                  <div
                    data-testid={`level3-pdp-progression-step-${i + 1}`}
                    className="flex items-center gap-2 px-3.5 py-2 rounded-full border text-[12px] sm:text-[13px] font-semibold uppercase tracking-wide transition-colors"
                    style={
                      isCurrent
                        ? { backgroundColor: accent, borderColor: accent, color: L.navy }
                        : { backgroundColor: "transparent", borderColor: "rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.55)" }
                    }
                  >
                    {isCurrent && <CheckCircle2 size={13} />}
                    {step}
                  </div>
                  {i < LEVEL3_SERIES.progression.length - 1 && (
                    <ArrowRight size={14} className="text-white/25 shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-[13px] sm:text-[14px] text-white/60 text-center max-w-lg mx-auto leading-[1.6]">
            Book {book.book_number} of 5 — {book.series_step}. Part of the five-book Level 3 journey from knowing yourself to building a life.
          </p>
        </div>
      </section>

      {/* PREVIOUS / NEXT + AUTHOR STRIP */}
      <section style={{ backgroundColor: L.cream }}>
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-16 md:py-20">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            {prevBook ? (
              <Link to={`/books/level3/${prevBook.slug}`} data-testid="level3-pdp-prev-book" className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: L.navy }}>
                <ArrowLeft size={14} /> Book {prevBook.book_number}: {prevBook.title}
              </Link>
            ) : <span />}
            {nextBook && (
              <Link to={`/books/level3/${nextBook.slug}`} data-testid="level3-pdp-next-book" className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: L.navy }}>
                Book {nextBook.book_number}: {nextBook.title} <ArrowRight size={14} />
              </Link>
            )}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 text-[12px] uppercase tracking-[0.16em] font-semibold" style={{ color: L.textMuted }}>
              LifeIQ Publications · An imprint of LifeIQ EdTech LLP · Author: Kunti Sudheer Bose
            </p>
          </Reveal>
        </div>
      </section>

      <StickyBuyBar
        product={book}
        label="Buy Now"
        source="level3-product-page-sticky"
        testId="level3-pdp-sticky-buy"
        accentColor={accent}
        barClassName="border-[#2A3C60]"
      />
    </main>
  );
}
